// @flow
import React, { Component } from "react"
import Helmet from "react-helmet"
import Img from "gatsby-image"
import GoDeviceCameraVideo from "react-icons/lib/go/device-camera-video"
import FaAngleDown from "react-icons/lib/fa/angle-down"
import { Motion, spring, presets } from "react-motion"
import { adjustFontSizeTo, rhythm } from "../utils/typography"
import {
  white,
  black,
  greyXLight,
  primary,
  primaryXLight,
  primaryLight,
  primaryMid,
  primaryDark,
  accentLight,
} from "../utils/colors"
import Section from "../components/Section"
import Button from "../components/Button"
import WhenInView from "../components/WhenInView"
import FeatureGallery from "../components/FeatureGallery"
import Testimonials from "../components/Testimonials"
import CustomerRatingBar from "../components/CustomerRatingBar"
import GALogos from "../components/GALogos"
import ContentSpotlight from "../components/ContentSpotlight"
import UpcomingWebinars from "../components/UpcomingWebinars"
import DemoBar from "../components/DemoBar"
import heroBg from "../images/home-hero.jpg"
import vp2Animation from "../images/vp2-animation.gif"
import vp3Screen from "../images/vp3-screen.png"
import vp3Device from "../images/vp3-device.svg"

type Props = {
  data: {
    spotlightBrokerInterview: FixedImage,
    davePhoto: FixedImage,
    nathanPhoto: FixedImage,
    jennPhoto: FixedImage,
    danielHeadshot: FixedImage,
    danHeadshot: FixedImage,
    willieHeadshot: FixedImage,
    aaronHeadshot: FixedImage,
    spotlightBrokerInterview: FluidImage,
    spotlightDigiGuide: FluidImage,
    spotlightBuildingSolution: FluidImage,
  },
}

class IndexPage extends Component<Props> {
  scrollTo: any => void
  scrollMore: () => void
  expandTarget: ?Node
  contentSpotlightLinks: Array<ContentSpotlightLink>

  constructor(props: Props) {
    super(props)
    this.contentSpotlightLinks = [
      {
        name: "Video: Broker Interview",
        image: this.props.data.spotlightBrokerInterview,
        url: "http://hr.easecentral.com/broker-testimonial-nathan-craig.html",
        video: true,
      },
      {
        name: "Free Guide: Agency Digital Marketing",
        image: this.props.data.spotlightDigiGuide,
        url: "http://hr.easecentral.com/AgencyDigitalMarketingGuide.html",
      },
      {
        name: "Free Guide: Building a Complete HR Solution with EaseCentral",
        image: this.props.data.spotlightBuildingSolution,
        url: "http://hr.easecentral.com/TheCompleteHRSolutionGuide.html",
      },
    ]
  }

  componentDidMount() {
    if (typeof document !== "undefined") {
      const script = document.createElement("script")
      script.src = "https://play.vidyard.com/DWoUfj7Hww3x8bPeBTPzsD.js?v=3.1.1&type=lightbox"
      script.async = true
      this.initSmoothScroll()
      if (document.body) {
        document.body.appendChild(script)
      } else {
        throw new Error("Unexpectedly missing <body>.")
      }
    }

    // require google optimize
    if (window.ga) {
      window.ga("require", "GTM-N5H7443")
    }
  }

  initSmoothScroll() {
    const SmoothScroll = require("smooth-scroll")
    const scroll = new SmoothScroll()
    this.expandTarget = document.getElementById("simplify-enrollment")
    this.scrollTo = element =>
      scroll.animateScroll(element, null, {
        header: "#header",
        offset: 48,
      })
  }

  scrollMore = () => {
    this.scrollTo(this.expandTarget)
  }

  playVideo() {
    // Don't track while developing.
    if (process.env.NODE_ENV === `production` && typeof ga === `function`) {
      window.ga(`send`, `event`, `Watch Video`, `click`, `Home Hero`)
    }
    // $FlowFixMe
    fn_vidyard_DWoUfj7Hww3x8bPeBTPzsD() // eslint-disable-line no-undef
  }

  render() {
    return (
      <div>
        <Helmet>
          <title>EaseCentral</title>
          <style type="text/css">{`.async-hide { opacity: 0 !important}`}</style>
          {/* <script>{`
            (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
            h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
            (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
            })(window,document.documentElement,'async-hide','dataLayer',4000, {'GTM-N5H7443':true});
          `}</script> */}
        </Helmet>
        <Section css={styles.hero} backgroundColor={black}>
          <div css={styles.heroContent}>
            <h1 css={styles.heroHeadline}>Your clients are ready for online enrollment.</h1>
            <p css={styles.heroSubheadline}>
              EaseCentral takes the headaches out of administration so you can focus on benefits.
            </p>
            <Button onClick={this.playVideo}>
              <GoDeviceCameraVideo
                width={18}
                height={18}
                color={`hsla(0, 0%, 0%, 0.25)`}
                style={{ marginRight: "0.675em" }}
              />
              Watch Video
            </Button>
            <div css={styles.seeMore}>
              <Button css={styles.seeMoreButton} onClick={this.scrollMore}>
                <FaAngleDown color={primaryDark} height={50} width={50} />
              </Button>
            </div>
          </div>
        </Section>
        <Section css={styles.vp1} backgroundColor={primaryLight}>
          <div css={styles.valueProp}>
            <div css={styles.valuePropContent} id="simplify-enrollment">
              <h3 css={styles.valuePropName}>Simplify Enrollment</h3>
              <p css={styles.valuePropBody}>
                Save time by eliminating paper forms, reducing errors, and creating digital employee
                files for you and your groups.
              </p>
              <div css={styles.testimonial}>
                <p css={styles.testimonialQuote}>
                  &ldquo;Our agency has used several online enrollment solutions, but none of them
                  have been so easy to use.&rdquo;
                </p>
                <div css={styles.testimonialSource}>
                  <div css={styles.testimonialImage}>
                    <Img resolutions={this.props.data.davePhoto.resolutions} />
                  </div>
                  <div>
                    <span css={styles.testimonialSourceText}>Dave Toebin,</span>
                    <span css={styles.testimonialSourceText}>Insight Insurance Services</span>
                  </div>
                </div>
              </div>
            </div>
            <div css={[styles.valuePropVisual, styles.vp1Visual]}>
              <video autoPlay muted loop playsInline>
                <source
                  src={__PATH_PREFIX__ + "/vp1-benefits-enrollment.webm"} // eslint-disable-line
                  type="video/webm;codecs='vp8'"
                />
                <source
                  src={__PATH_PREFIX__ + "/vp1-benefits-enrollment.mp4"} // eslint-disable-line
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </Section>
        <Section css={styles.vp2} backgroundColor={greyXLight}>
          <div css={styles.valueProp}>
            <div css={[styles.valuePropVisual, styles.vp2Visual]}>
              <img src={vp2Animation} />
            </div>
            <div css={styles.valuePropContent}>
              <h3 css={styles.valuePropName}>Increase Engagement</h3>
              <p css={styles.valuePropBody}>
                Deepen relationships with your clients by giving them a mobile app, integrating
                benefits with their payroll provider, and offering new HR and compliance services.
              </p>
              <div css={styles.testimonial}>
                <p css={styles.testimonialQuote}>
                  &ldquo;The mobile app makes every employee feel like they work for some Fortune
                  500 company.&rdquo;
                </p>
                <div css={styles.testimonialSource}>
                  <div css={styles.testimonialImage}>
                    <Img resolutions={this.props.data.nathanPhoto.resolutions} />
                  </div>
                  <div>
                    <span css={styles.testimonialSourceText}>Nathan Craig,</span>
                    <span css={styles.testimonialSourceText}>Culture Insurance Services</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
        <Section css={styles.vp3} backgroundColor={primaryLight}>
          <div css={styles.valueProp}>
            <div css={styles.valuePropContent}>
              <h3 css={styles.valuePropName}>Gain Visibility</h3>
              <p css={styles.valuePropBody}>
                Stay on top of enrollment status, access group information 24/7, and support your
                clients year round.
              </p>
              <div css={styles.testimonial}>
                <p css={styles.testimonialQuote}>
                  &ldquo;Something that&rsquo;s helped me through open enrollment is that I have
                  instantaneous results to see who had finished and am able to provide reporting to
                  the employer immediately.&rdquo;
                </p>
                <div css={styles.testimonialSource}>
                  <div css={styles.testimonialImage}>
                    <Img resolutions={this.props.data.jennPhoto.resolutions} />
                  </div>
                  <div>
                    <span css={styles.testimonialSourceText}>Jenn Knoop-Koblick,</span>
                    <span css={styles.testimonialSourceText}>Filice</span>
                  </div>
                </div>
              </div>
            </div>
            <WhenInView
              render={({ inView }) => (
                <div css={[styles.valuePropVisual, styles.vp3Visual]}>
                  <Motion
                    style={{
                      x: spring(inView ? 1 : 0, presets.gentle),
                    }}
                  >
                    {({ x }) => (
                      <img
                        style={{
                          opacity: x,
                          transform: `scale(${x})`,
                        }}
                        css={styles.vp3Screen}
                        src={vp3Screen}
                      />
                    )}
                  </Motion>
                  <img css={styles.vp3Device} src={vp3Device} />
                </div>
              )}
            />
          </div>
        </Section>
        <Section css={styles.featureGallery} backgroundColor={white}>
          <FeatureGallery />
        </Section>
        <Section css={styles.testimonials} backgroundColor={greyXLight}>
          <Testimonials
            danielHeadshot={this.props.data.danielHeadshot}
            danHeadshot={this.props.data.danHeadshot}
            willieHeadshot={this.props.data.willieHeadshot}
            aaronHeadshot={this.props.data.aaronHeadshot}
          />
        </Section>
        <Section css={styles.customerRatingBar} backgroundColor={primary}>
          <CustomerRatingBar />
        </Section>
        <Section css={styles.gaPartners} backgroundColor={white}>
          <div css={styles.gaPartnersInner}>
            <h3 css={styles.gaPartnersHeadline}>A Few Of Our GA Partners</h3>
            <GALogos />
          </div>
        </Section>
        <ContentSpotlight links={this.contentSpotlightLinks} />
        <UpcomingWebinars />
        <DemoBar backgroundColor={primaryXLight} />
      </div>
    )
  }
}

const styles = {
  hero: {
    backgroundImage: `url(${heroBg})`,
    backgroundSize: "cover",
    backgroundPosition: "20%",
    color: "#fff",
    paddingTop: rhythm(4),
    paddingBottom: "23%",
    position: "relative",
    overflow: "hidden",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-95%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: primaryLight,
      borderTop: `${rhythm()} solid hsla(203, 76%, 78%, 1)`,
      transform: "skew(0, -10deg)",
    },
    "@media screen and (min-width: 540px)": {
      backgroundPosition: "left",
    },
    "@media screen and (min-width: 820px)": {
      paddingTop: rhythm(7),
    },
  },
  heroContent: {
    width: "80%",
    marginBottom: rhythm(2),
    "@media screen and (min-width: 620px)": {
      width: "100%",
      maxWidth: "30em",
      marginBottom: "0",
    },
  },
  heroHeadline: {
    "@media screen and (max-width: 619px)": {
      fontSize: "6.75vw",
    },
  },
  heroSubheadline: {
    "@media screen and (min-width: 620px)": {
      ...adjustFontSizeTo("20px"),
    },
  },
  seeMore: {
    display: "block",
    position: "absolute",
    bottom: "4.5%",
    right: "0",
    width: "200%",
    height: rhythm(),
    borderTop: `${rhythm()} solid ${primary}`,
    transform: "skew(0, -10deg)",
    zIndex: "10",
  },
  seeMoreButton: {
    height: rhythm(3),
    width: rhythm(3),
    borderRadius: "10rem",
    color: primaryDark,
    backgroundColor: primary,
    boxShadow: "0 4px 24px 0 rgba(0, 0, 0, 0.1)",
    position: "absolute",
    bottom: `-${rhythm(5 / 4)}`,
    left: "75%",
    marginLeft: `-${rhythm(3 / 2)}`,
    transform: "skew(0, 10deg) !important",
    zIndex: "10",
    "&:hover": {
      backgroundColor: primary,
      marginBottom: "2px",
      boxShadow: "0 8px 24px rgba(50,50,93,.1), 0 8px 15px rgba(0,0,0,.07)",
    },
    "&:focus": {
      outline: "none",
    },
    "@media screen and (max-width: 560px)": {
      display: "none",
    },
  },
  valueProp: {
    display: "flex",
    flexDirection: "column",
    padding: `${rhythm()} 0`,
    "@media screen and (min-width: 700px)": {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  valuePropVisual: {
    flex: "1 1 auto",
    order: "1",
    "@media screen and (min-width: 700px)": {
      order: "0",
    },
  },
  valuePropContent: {
    flex: "1 1 auto",
    order: "2",
    "@media screen and (min-width: 700px)": {
      order: "0",
      maxWidth: "23.5em",
    },
  },
  valuePropName: {
    color: primaryDark,
    fontWeight: 700,
    "@media screen and (max-width: 699px)": {
      ...adjustFontSizeTo("24px"),
    },
  },
  valuePropBody: {
    color: "rgba(0,0,0,0.54)",
  },
  vp1: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "17%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      left: "0",
      width: "200%",
      height: "100%",
      backgroundColor: greyXLight,
      transform: "skew(0, 10deg)",
    },
  },
  vp1Visual: {
    " video": {
      width: "100%",
      maxWidth: "35.5rem",
      boxShadow: "4px 4px 24px 0 rgba(0, 0, 0, 0.15)",
      marginBottom: rhythm(),
    },
    "@media screen and (min-width: 700px)": {
      textAlign: "right",
      paddingLeft: rhythm(),
    },
  },
  vp2: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "10%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: primaryLight,
      transform: "skew(0, -7deg)",
    },
  },
  vp2Visual: {
    maxWidth: rhythm(23),
    "> img": {
      boxShadow: "4px 4px 24px 0 rgba(0, 0, 0, 0.15)",
    },
    "@media screen and (min-width: 700px)": {
      textAlign: "left",
      paddingRight: rhythm(),
    },
  },
  vp3: {
    position: "relative",
    overflow: "hidden",
    paddingTop: rhythm(2),
    paddingBottom: "15%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: white,
      transform: "skew(0, -7deg)",
    },
  },
  vp3Visual: {
    position: "relative",
    "@media screen and (min-width: 700px)": {
      textAlign: "right",
      marginLeft: rhythm(3 / 2),
    },
  },
  vp3Screen: {
    position: "absolute",
    top: `-${rhythm(2)}`,
    left: "0",
    width: rhythm(19),
    maxWidth: "80%",
    boxShadow: "20px 20px 24px rgba(50,50,93,.1), 20px 20px 15px rgba(0,0,0,.07)",
    "@media screen and (min-width: 700px)": {
      left: "-3%",
    },
    "@media screen and (min-width: 900px)": {
      left: "auto",
      top: `-${rhythm(5 / 2)}`,
      right: rhythm(5),
    },
  },
  vp3Device: {},
  testimonial: {
    ":before": {
      content: "''",
      display: "block",
      width: "48px",
      borderTop: `2px solid ${primaryMid}`,
      marginBottom: rhythm(),
    },
  },
  testimonialQuote: {
    color: primary,
    fontWeight: 600,
    fontStyle: "italic",
  },
  testimonialSource: {
    display: "flex",
    alignItems: "center",
    marginBottom: rhythm(),
  },
  testimonialImage: {
    width: "2.625em",
    height: "2.625em",
    marginRight: rhythm(1 / 2),
    " img": {
      borderRadius: "2em",
      margin: 0,
    },
  },
  testimonialSourceText: {
    ...adjustFontSizeTo("15px"),
    display: "block",
    color: primaryDark,
  },
  featureGallery: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "10%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: greyXLight,
      transform: "skew(0, -7deg)",
    },
  },
  testimonials: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "8%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: "hsla(200, 90%, 49%, 0.99)",
      transform: "skew(0, -3deg)",
    },
  },
  customerRatingBar: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "4%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: white,
      transform: "skew(0, -3deg)",
    },
  },
  gaPartners: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "4%",
    "&:after": {
      content: "' '",
      display: "block",
      position: "absolute",
      bottom: "-100%",
      right: "0",
      width: "200%",
      height: "100%",
      backgroundColor: accentLight,
      transform: "skew(0, -3deg)",
    },
  },
  gaPartnersInner: {
    padding: `${rhythm(3)} 0`,
  },
  gaPartnersHeadline: {
    textAlign: "center",
    color: primaryDark,
  },
}

export default IndexPage

// eslint-disable-next-line
export const query = graphql`
  query IndexQuery {
    davePhoto: imageSharp(id: { regex: "/dave-toebin.png/" }) {
      resolutions(width: 42) {
        ...GatsbyImageSharpResolutions
      }
    }
    nathanPhoto: imageSharp(id: { regex: "/nathan-craig.png/" }) {
      resolutions(width: 42) {
        ...GatsbyImageSharpResolutions
      }
    }
    jennPhoto: imageSharp(id: { regex: "/jenn-knoop-koblick.png/" }) {
      resolutions(width: 42) {
        ...GatsbyImageSharpResolutions
      }
    }
    danielHeadshot: imageSharp(id: { regex: "/daniel.png/" }) {
      resolutions(width: 52) {
        ...GatsbyImageSharpResolutions
      }
    }
    danHeadshot: imageSharp(id: { regex: "/dan.png/" }) {
      resolutions(width: 52) {
        ...GatsbyImageSharpResolutions
      }
    }
    willieHeadshot: imageSharp(id: { regex: "/willie.png/" }) {
      resolutions(width: 52) {
        ...GatsbyImageSharpResolutions
      }
    }
    aaronHeadshot: imageSharp(id: { regex: "/aaron.png/" }) {
      resolutions(width: 52) {
        ...GatsbyImageSharpResolutions
      }
    }
    spotlightBrokerInterview: imageSharp(id: { regex: "/spotlight-broker-interview.jpg/" }) {
      sizes(maxWidth: 304, quality: 100) {
        ...GatsbyImageSharpSizes
      }
    }
    spotlightDigiGuide: imageSharp(id: { regex: "/spotlight-digi-guide.jpg/" }) {
      sizes(maxWidth: 304, quality: 100) {
        ...GatsbyImageSharpSizes
      }
    }
    spotlightBuildingSolution: imageSharp(id: { regex: "/spotlight-building-solution.jpg/" }) {
      sizes(maxWidth: 304, quality: 100) {
        ...GatsbyImageSharpSizes
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/pages/index.js